import {
  HttpClient,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ChangeOfProduct } from '#interfaces/change-of-product/change-of-product.interface';
import {
  ApiResponseData,
  CreatePaymentOrderResponse,
  PaymentOrderResponse, PaymentOrdersResponse, QuotationResponse,
  SendEmailResponse
} from '#interfaces/http-response/responses/http-responses';
import { PaymentOrderApprovalSnapshot } from '#interfaces/payment-orders-approvals/payment-orders-approvals.interface';
import { PagedResponse } from '#interfaces/http-response/responses/api-response.interface';
import { PaymentOrderInvoiceDocument } from '#interfaces/payment-orders/payment-order-invoice-document.interface';
import { PendingInvoicePaymentOrder, VerifyPendingInvoicesResult } from '#interfaces/payment-orders/pending-invoice-payment-order.interface';
import {
  CreatePaymentOrder,
  PayPaymentOrder,
  PaymentOrderDiscount,
  PaymentOrderFeeCondoneHistory,
  PaymentOrderInvoice,
  UpdatePaymentOrderCommand
} from '#interfaces/payment-orders/payment-orders.interface';
import { PaymentReceipt } from '#interfaces/payment-receipts/payment-receipt.interface';
import { ResumePaymentDto } from '#interfaces/payment-receipts/resume-payment.interface';
import { DeclineCancelRequestModalOptions } from '#root/modules/admin/crm/payment-orders/pages/details/components/decline-cancel-request/decline-cancel-request.component';
import { HeadersHttpService } from '#utils/http-headers/http-headers.service';
import { environment } from 'environments/environment';
import {
  BehaviorSubject,
  Observable,
  catchError,
  map,
  of,
  switchMap,
  tap,
  throwError
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PaymentOrdersService {

  private _paymentOrders: BehaviorSubject<PaymentOrdersResponse | null> = new BehaviorSubject(null);
  private _paymentOrder: BehaviorSubject<PaymentOrderResponse | null> = new BehaviorSubject(null);
  private _paymentOrdersCustomer: BehaviorSubject<PaymentOrdersResponse | null> = new BehaviorSubject(null);

  private readonly baseUrl: string = environment.baseUrl + 'PaymentOrder/';

  constructor(private readonly httpClient: HttpClient, private readonly httpHeaders: HeadersHttpService) { }

  get paymentOrders$(): Observable<PaymentOrdersResponse> {
    return this._paymentOrders.asObservable();
  }

  get paymentOrder$(): Observable<PaymentOrderResponse> {
    return this._paymentOrder.asObservable();
  }

  get paymentOrdersCustomer$(): Observable<PaymentOrdersResponse> {
    return this._paymentOrdersCustomer.asObservable();
  }

  GetAll(page: number,
    size: number,
    name: string = '',
    sort: string = 'asc',
    column: string = 'Code',
    all: boolean = false,
    projectId: number = Number(localStorage.getItem("selectedProjectId")))
    : Observable<PaymentOrdersResponse> {
    return this.httpClient.get<PaymentOrdersResponse>(this.baseUrl + "GetAll", {
      headers: this.httpHeaders.getHeadersPrivate(), params: {
        pageNumber: page,
        pageSize: size,
        parameter: name,
        order: sort,
        column: column,
        all: all,
        projectId: projectId
      }
    }).pipe(
      tap((res) => { this._paymentOrders.next(res); }),
      catchError(err => throwError(() => err))
    );
  }

  GetAllWithdrawal(page: number,
    size: number,
    name: string = '',
    sort: string = 'asc',
    column: string = 'Code',
    all: boolean = false,
    projectId: number = Number(localStorage.getItem("selectedProjectId"))) {
    return this.httpClient.get<PaymentOrdersResponse>(`${this.baseUrl}GetAllWithdrawals`, {
      params: {
        pageNumber: page,
        pageSize: size,
        parameter: name,
        order: sort,
        column: column,
        all: all,
        projectId: projectId
      }, headers: this.httpHeaders.getHeadersPrivate()
    }).pipe(
      tap((value) => { this._paymentOrders.next(value); }),
      catchError((err) => throwError(() => err))
    );
  }

  Create(payload: CreatePaymentOrder): Observable<CreatePaymentOrderResponse> {
    return this.httpClient.post<CreatePaymentOrderResponse>(this.baseUrl + 'Create', payload, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  Update(payload: UpdatePaymentOrderCommand): Observable<PaymentOrderResponse> {
    return this.httpClient.post<PaymentOrderResponse>(this.baseUrl + 'Update', payload, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  GetById(id: number): Observable<PaymentOrderResponse> {
    return this.httpClient.get<PaymentOrderResponse>(this.baseUrl + "GetById/" + id, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap((res) => {
          if (res.data.changeProductRequest) {
            return this.httpClient.get<ApiResponseData<ChangeOfProduct>>(this.baseUrl + 'ChangeOfProduct/' + id, { headers: this.httpHeaders.getHeadersPrivate() })
              .pipe(
                map((getChangeProductRequest) => {
                  let activeRequest = null;
                  if (getChangeProductRequest.data.paymentOrderId === res.data.id) {
                    activeRequest = getChangeProductRequest.data;
                  }
                  return { ...res, data: { ...res.data, activeChangeProductRequest: activeRequest } };
                })
              );
          } else {
            return of(res);
          }
        }),
        tap((res) => { this._paymentOrder.next(res); }),
        catchError(err => throwError(() => err))
      );
  }

  Pay(payload: PayPaymentOrder): Observable<ApiResponseData<PayPaymentOrder>> {
    const signature = localStorage.getItem('signature');
    return this.httpClient.post<ApiResponseData<PayPaymentOrder>>(environment.baseUrl + 'Payment/Create', { ...payload, signature: signature === 'null' ? null : signature }, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  CreateMassivePayment(payload: PayPaymentOrder, showModal = true): Observable<ApiResponseData<PaymentReceipt>> {
    const signature = localStorage.getItem('signature');
    return this.httpClient.post<ApiResponseData<PaymentReceipt>>(environment.baseUrl + 'Payment/CreateMassivePayment', { ...payload, signature: signature === 'null' ? null : signature }, { headers: this.httpHeaders.getHeadersPrivate({ showModal }) })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  WithdrawalRequest(payload: { id: number; withdrawalRequest: string; paymentOrderImages: Array<{ name: string; url: string; paymentOrderId: number }>; signature: string; withdrawalObservation: string }) {
    return this.httpClient.put<ApiResponseData<PayPaymentOrder>>(this.baseUrl + 'WithdrawalRequest', payload, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  ApproveWithdrawalRequest(id: number, signature: string) {
    return this.httpClient.put<ApiResponseData<PayPaymentOrder>>(this.baseUrl + 'ApproveWithdrawalRequest', { id, signature }, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  PenaltyRequest(payload: { productName: string; id: number; typePenalty: string; penaltyAmount: number; penaltyPercentage: number; file: Blob }) {
    const form = new FormData();
    form.append('id', payload.id.toString());
    form.append('typePenalty', payload.typePenalty);
    form.append('penaltyAmount', payload.penaltyAmount.toString());
    form.append('penaltyPercentage', payload.penaltyPercentage.toString());
    form.append('file', payload.file, `Solicitud de retiro - Inmueble ${payload.productName}.pdf`);

    return this.httpClient.put<ApiResponseData<PayPaymentOrder>>(this.baseUrl + 'PenaltyRequest', form, { headers: this.httpHeaders.getHeadersFormDataPrivate() })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  WithdrawalCheck(payload: { id: number; withdrawalCheck: string; withdrawalCheckBank: string; withdrawalCheckAmount: number; settlement: string; withdrawalCheckDate: string; withdrawalObservation: string; withdrawalCheckFile: string }) {
    return this.httpClient.put<ApiResponseData<PayPaymentOrder>>(this.baseUrl + 'WithdrawalCheck', payload, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  ApprovePenaltyRequest(id: number) {
    return this.httpClient.put<ApiResponseData<PayPaymentOrder>>(this.baseUrl + 'ApprovePenaltyRequest', { id }, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  WithdrawalBankCheckRequest(id: number) {
    return this.httpClient.put<ApiResponseData<PayPaymentOrder>>(this.baseUrl + 'WithdrawalBankCheckRequest', { id }, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  CancellationRequest(payload: { id: number; paymentOrderId: number; observation: string }) {
    return this.httpClient.put<QuotationResponse>(environment.baseUrl + 'Payment/CancelRequest', payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  Cancel(payload: { id: number; paymentOrderId: number; }) {
    return this.httpClient.put<QuotationResponse>(environment.baseUrl + 'Payment/Cancel', payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  CreateExtraordinaryPayment(payload: PayPaymentOrder): Observable<ApiResponseData<PayPaymentOrder>> {
    const signature = localStorage.getItem('signature');
    return this.httpClient.post<ApiResponseData<PayPaymentOrder>>(environment.baseUrl + 'Payment/CreateExtraordinary', { ...payload, signature: signature === 'null' ? null : signature }, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap(resp => of(resp)),
        catchError(err => throwError(() => err))
      );
  }

  CancellationRequestExtraordinary(payload: { id: number; paymentOrderId: number; observation: string }) {
    return this.httpClient.put<QuotationResponse>(environment.baseUrl + 'ExtraordinaryPayment/CancelRequest', payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  CancelExtraordinary(payload: { id: number; paymentOrderId: number; }) {
    return this.httpClient.put<QuotationResponse>(environment.baseUrl + 'ExtraordinaryPayment/Cancel', payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  DeclineCancelRequest(payload: DeclineCancelRequestModalOptions & { observation: string }) {
    return this.httpClient.put<QuotationResponse>(environment.baseUrl + `Payment/CancelCancellationRequest/${payload.id}`, payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  ChangeDate(payload: { id: number; initDate: string }) {
    return this.httpClient.put<QuotationResponse>(this.baseUrl + `AdjustmentPaymentDate/${payload.id}`, payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  ChangeAdvisor(payload: { id: number, opportunityId: number, userId: string }) {
    return this.httpClient.put<QuotationResponse>(this.baseUrl + `ChangeSalesAdvisor/${payload.id}`, payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  ChangeCustomer(payload: { id: number, opportunityId: number, customerId: string; observation: string }) {
    return this.httpClient.put<QuotationResponse>(this.baseUrl + `ChangeCustomer/${payload.id}`, payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  GetPaymentOrderByCustomer(customerId: string, reset?: boolean): Observable<PaymentOrdersResponse> {
    if (this._paymentOrdersCustomer.value && this._paymentOrdersCustomer.value.data) {
      return of(this._paymentOrdersCustomer.value);
    }

    return this.httpClient.get<PaymentOrdersResponse>(this.baseUrl + "GetPaymentOrderByCustomer", {
      headers: this.httpHeaders.getHeadersPrivate(),
      params: { customerId }
    }).pipe(
      tap((res) => { this._paymentOrdersCustomer.next(res); }),
      catchError(err => throwError(() => err))
    );
  }

  ChangePeriod(payload: { id: number, numFees: number; period: number }) {
    return this.httpClient.put<PaymentOrderResponse>(this.baseUrl + `ChangePeriod/` + payload.id, payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  reset() {
    this._paymentOrdersCustomer.next(null);
  }

  ActiveChangeOfProduct(paymentOrderId: number) {
    return this.httpClient.get<ApiResponseData<ChangeOfProduct>>(this.baseUrl + 'ChangeOfProduct/' + paymentOrderId, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        switchMap(resp => {
          const actualPaymentOrder = this._paymentOrder.value;

          if (!actualPaymentOrder) {
            throwError(() => new HttpErrorResponse({ status: 404, statusText: 'No se encontró la orden de pago activa' }));
          }

          if (resp.data.paymentOrderId !== actualPaymentOrder.data.id) {
            throwError(() => new HttpErrorResponse({ status: 404, statusText: 'La orden de pago activa no coincide con la orden de pago de la solicitud' }));
          }

          this._paymentOrder.next({ ...actualPaymentOrder, data: { ...actualPaymentOrder.data, activeChangeProductRequest: resp.data } });
          return of(resp);
        }),
        catchError(err => throwError(() => err))
      );
  }

  GetPaymentsInTheWeek(projectId: number): Observable<ApiResponseData<ResumePaymentDto[]>> {
    return this.httpClient.get<ApiResponseData<ResumePaymentDto[]>>(this.baseUrl + "GetPaymentsInTheWeek/" + projectId, { headers: this.httpHeaders.getHeadersPrivate() })
      .pipe(
        tap(() => {}),
        catchError(err => throwError(() => err))
      );
  }

  SendPaymentReminder(payload: { selectedPaymentOrders: { projectId: number; paymentOrdersIds: number[] } }) {
    return this.httpClient.post<SendEmailResponse>(
      this.baseUrl + `SendPaymentReminder`,
      payload,
      { headers: this.httpHeaders.getHeadersPrivate() }
    ).pipe(
      map(data => data),
      catchError(err => throwError(() => err))
    );
  }

  SendEmailAccountStatus(paymentOrderIds: number[]) {
    const payload = {
      selectedPaymentOrders: {
        paymentOrdersIds: paymentOrderIds
      }
    };

    return this.httpClient.post<SendEmailResponse>(
      this.baseUrl + `SendEmailAccountStatus`,
      payload,
      { headers: this.httpHeaders.getHeadersPrivate() }
    ).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  SendPaymentReceipt(payload: { paymentId: number; paymentOrderId: number; paymentReceipt: File; }, showModal = true) {
    const formData = new FormData();
    formData.append('paymentId', payload.paymentId.toString());
    formData.append('paymentOrderId', payload.paymentOrderId.toString());
    formData.append('file', payload.paymentReceipt);

    return this.httpClient.post<ApiResponseData<boolean>>(this.baseUrl + 'SendPaymentReceipt', formData, { headers: this.httpHeaders.getHeadersFormDataPrivate({ showModal }) })
      .pipe(
        map((resp) => resp),
        catchError((err) => throwError(() => err))
      );
  }

  SetCustomFee(payload: { id: number, numFees: number, period: number; fees: ({ id: number; total: number })[] }) {
    return this.httpClient.put<PaymentOrderResponse>(this.baseUrl + `SetCustomFee/` + payload.id, payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  SetTaxRateReserve(payload: { id: number; taxRate: number; reserveDate: string }) {
    return this.httpClient.put<PaymentOrderResponse>(this.baseUrl + `SetTaxRateReserve/` + payload.id, payload, { headers: this.httpHeaders.getHeadersPrivate() }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  GetPdfAccountStatus(payload: { id: number }) {
    return this.httpClient.get<ApiResponseData<{ url: string; fileName: string }>>(this.baseUrl + `GetPdfAccountStatus/` + payload.id, { headers: this.httpHeaders.getHeadersPrivate({ showModal: true }) }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  GetPdfPaymentPlan(payload: { id: number }) {
    return this.httpClient.get<ApiResponseData<{ url: string; fileName: string }>>(this.baseUrl + `GetPdfPaymentPlan/` + payload.id, { headers: this.httpHeaders.getHeadersPrivate({ showModal: true }) }).pipe(
      map((data) => data),
      catchError((err) => throwError(() => err))
    );
  }

  setChangeOfProductToNull() {
    const actualPaymentOrder = this._paymentOrder.value;

    if (!actualPaymentOrder) {
      throwError(() => new HttpErrorResponse({ status: 404, statusText: 'No se encontró la orden de pago activa' }));
    }

    this._paymentOrder.next({ ...actualPaymentOrder, data: { ...actualPaymentOrder.data, activeChangeProductRequest: null } });
  }

  ApprovePaymentOrder(id: number): Observable<ApiResponseData<string>> {
    return this.httpClient.put<ApiResponseData<string>>(
      this.baseUrl + `Approve/${id}`,
      {},
      { headers: this.httpHeaders.getHeadersPrivate() }
    ).pipe(
      map(data => data),
      catchError(err => throwError(() => err))
    );
  }

  VerifyPendingInvoices(force: boolean = false): Observable<ApiResponseData<VerifyPendingInvoicesResult>> {
    return this.httpClient.post<ApiResponseData<VerifyPendingInvoicesResult>>(
      this.baseUrl + 'VerifyPendingInvoices',
      { force },
      { headers: this.httpHeaders.getHeadersPrivate({ showModal: false }) }
    ).pipe(
      map(data => data),
      catchError(err => throwError(() => err))
    );
  }

  GetVerifyPendingInvoicesStatus(): Observable<ApiResponseData<VerifyPendingInvoicesResult>> {
    return this.httpClient.get<ApiResponseData<VerifyPendingInvoicesResult>>(
      this.baseUrl + 'VerifyPendingInvoices/Status',
      { headers: this.httpHeaders.getHeadersPrivate() }
    ).pipe(
      map(data => data),
      catchError(err => throwError(() => err))
    );
  }

  SyncInvoice(id: number, forceRefresh: boolean = false): Observable<ApiResponseData<PaymentOrderInvoice>> {
    return this.httpClient.post<ApiResponseData<PaymentOrderInvoice>>(
      this.baseUrl + `SyncInvoice/${id}?forceRefresh=${forceRefresh}`,
      {},
      { headers: this.httpHeaders.getHeadersPrivate() }
    ).pipe(
      map(data => data),
      catchError(err => throwError(() => err))
    );
  }

  GetInvoiceDocument(id: number): Observable<ApiResponseData<PaymentOrderInvoiceDocument>> {
    return this.httpClient.get<ApiResponseData<PaymentOrderInvoiceDocument>>(
      this.baseUrl + `Invoice/${id}`,
      { headers: this.httpHeaders.getHeadersPrivate() }
    ).pipe(
      map(data => data),
      catchError(err => throwError(() => err))
    );
  }

  GetPendingInvoices(options: {
    pageNumber: number
    pageSize: number
    parameter?: string
    order?: string
    column?: string
    projectId?: number | null
    onlySettled?: boolean
  }): Observable<PagedResponse<PendingInvoicePaymentOrder>> {
    const params: Record<string, string | number | boolean> = {
      pageNumber: options.pageNumber,
      pageSize: options.pageSize,
      parameter: options.parameter ?? '',
      order: options.order ?? '',
      column: options.column ?? '',
      onlySettled: options.onlySettled ?? true
    };

    if (options.projectId != null) params['projectId'] = options.projectId;

    return this.httpClient.get<PagedResponse<PendingInvoicePaymentOrder>>(
      this.baseUrl + 'PendingInvoices',
      { headers: this.httpHeaders.getHeadersPrivate(), params }
    ).pipe(
      map(data => data),
      catchError(err => throwError(() => err))
    );
  }

  RequestCondoneOverdue(payload: {
    paymentOrderId: number;
    items: { feeId: number; condoneAmount: number }[];
    reason: string;
  }): Observable<ApiResponseData<string>> {
    return this.httpClient
      .put<ApiResponseData<string>>(
        this.baseUrl + 'RequestCondoneOverdue',
        payload,
        { headers: this.httpHeaders.getHeadersPrivate() }
      )
      .pipe(
        map((data) => data),
        catchError((err) => throwError(() => err))
      );
  }

  ApproveCondoneOverdue(payload: {
    paymentOrderId: number;
    items: { feeId: number; condoneAmount: number }[];
  }): Observable<ApiResponseData<string>> {
    return this.httpClient
      .put<ApiResponseData<string>>(
        this.baseUrl + 'ApproveCondoneOverdue',
        payload,
        { headers: this.httpHeaders.getHeadersPrivate() }
      )
      .pipe(
        map((data) => data),
        catchError((err) => throwError(() => err))
      );
  }

  RejectCondoneOverdue(payload: {
    paymentOrderId: number;
    feeIds: number[];
  }): Observable<ApiResponseData<string>> {
    return this.httpClient
      .put<ApiResponseData<string>>(
        this.baseUrl + 'RejectCondoneOverdue',
        payload,
        { headers: this.httpHeaders.getHeadersPrivate() }
      )
      .pipe(
        map((data) => data),
        catchError((err) => throwError(() => err))
      );
  }

  UpdatePaymentOrder(payload: {
    paymentOrderId: number;
    period: number;
    initDate: string;
    delinquencyPercentage: number;
    toleranceDays: number;
    totalPremium: number;
    graceMonths: number;
    bombFees: { feeNumber: number; amount: number }[];
    remainingFees?: { amount: number; paymentDate: string }[];
    fees: {
      feeNumber: number;
      amount: number;
      estimatedDate: Date;
      isGrace: boolean;
      isBomb: boolean;
    }[];
  }): Observable<ApiResponseData<string>> {
    return this.httpClient
      .put<ApiResponseData<string>>(
        this.baseUrl + 'UpdatePaymentOrder',
        payload,
        { headers: this.httpHeaders.getHeadersPrivate() }
      )
      .pipe(
        map((data) => data),
        catchError((err) => throwError(() => err))
      );
  }

  AddRemainingFee(payload: {
    paymentOrderId: number;
    remainingFees: { amount: number; paymentDate: string }[];
  }): Observable<ApiResponseData<string>> {
    return this.httpClient
      .put<ApiResponseData<string>>(
        this.baseUrl + 'AddRemainingFee',
        payload,
        { headers: this.httpHeaders.getHeadersPrivate() }
      )
      .pipe(
        map((data) => data),
        catchError((err) => throwError(() => err))
      );
  }

  GetPaymentOrderDiscounts(paymentOrderId: number): Observable<ApiResponseData<PaymentOrderDiscount[]>> {
    return this.httpClient
      .get<ApiResponseData<PaymentOrderDiscount[]>>(
        this.baseUrl + `GetPaymentOrderDiscounts/${paymentOrderId}`,
        { headers: this.httpHeaders.getHeadersPrivate() }
      )
      .pipe(
        map((data) => data),
        catchError((err) => throwError(() => err))
      );
  }  

  RejectPaymentOrder(payload: { id: number; reason: string | null }): Observable<ApiResponseData<string>> {
    return this.httpClient
      .put<ApiResponseData<string>>(
        this.baseUrl + `Reject/${payload.id}`,
        payload,
        { headers: this.httpHeaders.getHeadersPrivate() }
      )
      .pipe(
        map(data => data),
        catchError(err => throwError(() => err))
      );
  }

  GetPendingSnapshot(id: number): Observable<ApiResponseData<PaymentOrderApprovalSnapshot>> {
    return this.httpClient
      .get<ApiResponseData<PaymentOrderApprovalSnapshot>>(
        this.baseUrl + `GetPendingSnapshot/${id}`,
        { headers: this.httpHeaders.getHeadersPrivate() }
      )
      .pipe(
        map(data => data),
        catchError(err => throwError(() => err))
      );
  }

  GetCondoneHistory(paymentOrderId: number): Observable<ApiResponseData<PaymentOrderFeeCondoneHistory[]>> {
    return this.httpClient
      .get<ApiResponseData<PaymentOrderFeeCondoneHistory[]>>(
        this.baseUrl + `GetByPaymentOrder/${paymentOrderId}`,
        { headers: this.httpHeaders.getHeadersPrivate() }
      )
      .pipe(
        map(data => data),
        catchError(err => throwError(() => err))
      );
  }
}
